// EditorAPI — unified API layer for MCP bridge and external consumers
// Wraps existing singletons (GardenViewer, gardenObjectState, api, saveService, undoStack)

import { api } from './client'
import { getPlantDefinition } from './plantDefinitions'
import { saveService } from './SaveService'
import { gardenObjectState } from '@/state/GardenObjectState'
import { zoneAPI } from '@/zones/ZoneAPI'
import { isPointInZone, calculateZoneArea, distanceToZoneBoundary } from '@/utils/geometry'
import { undoStack } from '@/editor/UndoStack'
import { DPM } from '@/constants/coordinates'
import { isEditMode } from '@/utils/editMode'
import type { GardenViewer } from '@/GardenViewer'
import type { Layer, Product } from '@/types'

// In-memory reference photo store (session-only, persisted to API later)
interface ReferencePhoto {
  id: string
  label: string
  dataUri: string
}

let photoIdCounter = 0
const referencePhotos: ReferencePhoto[] = []

class EditorAPIManager {
  private viewer: GardenViewer | null = null

  /**
   * Wire up the viewer instance (called from GardenViewer.initialize)
   */
  setViewer(viewer: GardenViewer): void {
    this.viewer = viewer
  }

  private requireViewer(): GardenViewer {
    if (!this.viewer) throw new Error('EditorAPI not initialized — viewer not set')
    return this.viewer
  }

  private requireEditMode(): void {
    if (!isEditMode()) throw new Error('Write operations require edit mode (?edit=true)')
  }

  // ── Garden info ──

  getGardenInfo() {
    const viewer = this.requireViewer()
    const garden = viewer.getGarden()
    const stats = viewer.getStats()
    const outline = viewer.getSchemeOutline()

    return {
      id: garden?.id ?? '',
      name: garden?.name ?? '',
      description: garden?.description ?? '',
      rotation: garden?.rotation ?? 0,
      isEditable: garden?.isEditable ?? false,
      stats,
      boundary: outline ? {
        x: outline.x,
        y: outline.y,
        points: outline.points,
      } : null,
    }
  }

  setGardenRotation(degrees: number) {
    this.requireEditMode()
    const viewer = this.requireViewer()
    viewer.setGardenRotation(degrees)
    return { success: true }
  }

  async setGardenDescription(description: string) {
    this.requireEditMode()
    const viewer = this.requireViewer()
    const garden = viewer.getGarden()
    if (!garden) throw new Error('No garden loaded')
    await api.updateGarden(garden.id, {}, { description })
    garden.description = description
    return { success: true, description }
  }

  // ── Objects (GardenObjectState) ──

  getObjects(filter?: { layer?: Layer }) {
    if (filter?.layer) {
      const viewer = this.requireViewer()
      const categoryLayerMap = viewer.getCategoryLayerMap()
      return gardenObjectState.getObjectsByLayer(filter.layer, categoryLayerMap)
    }
    return gardenObjectState.getObjects()
  }

  addObject(params: {
    type: string
    productId: string
    x: number
    y: number
    width: number
    height: number
    rotation?: number
    elevation?: number
    label?: string
    points?: Array<{ x: number; y: number }>
    pathWaypoints?: Array<{ x: number; y: number }>
    pathWidth?: number
    zoneId?: string
  }) {
    this.requireEditMode()
    const { type, productId, x, y, width, height, zoneId, ...opts } = params
    const id = gardenObjectState.addObject(type, productId, x, y, width, height, opts)
    const snapshot = { ...gardenObjectState.getObjectById(id)! }
    undoStack.push({
      description: `Create object ${id}`,
      undo: () => gardenObjectState.deleteObject(id),
      redo: () => gardenObjectState.restoreObject(snapshot),
    })
    const result: { id: string; success: true; warnings?: string[] } = { id, success: true as const }
    if (zoneId) {
      const warnings = this.validateZonePlacement(x, y, zoneId)
      if (warnings.length > 0) result.warnings = warnings
    }
    return result
  }

  private validateZonePlacement(objectX: number, objectY: number, zoneId: string): string[] {
    const warnings: string[] = []
    const zone = zoneAPI.getZoneById(zoneId)
    if (!zone) {
      warnings.push(`Zone '${zoneId}' not found — placement succeeded without validation`)
      return warnings
    }
    if (!isPointInZone(objectX, objectY, zone)) {
      const distM = distanceToZoneBoundary(objectX, objectY, zone, DPM)
      warnings.push(`Object placed outside zone '${zoneId}' boundary (${distM.toFixed(1)}m away from nearest edge)`)
    }
    return warnings
  }

  /**
   * Resolve a product query to a product ID. Returns the product if unambiguous,
   * or throws with top candidates if ambiguous.
   */
  async resolveProduct(query: string): Promise<Product> {
    const result = await api.searchProducts({ search: query, perPage: 5 } as Parameters<typeof api.searchProducts>[0])
    if (result.products.length === 0) {
      throw new Error(`No products found for query: "${query}"`)
    }
    // Exact name match (case-insensitive)
    const exact = result.products.find(
      p => p.name.toLowerCase() === query.toLowerCase() ||
           p.code?.toLowerCase() === query.toLowerCase()
    )
    if (exact) return exact
    // If only one result, use it
    if (result.products.length === 1) return result.products[0]
    // Ambiguous — return candidates
    const candidates = result.products.slice(0, 3).map(p => formatProduct(p))
    throw new Error(
      `Ambiguous product query "${query}". Top candidates:\n${JSON.stringify(candidates, null, 2)}\n\nPlease use a specific productId from the candidates above.`
    )
  }

  /**
   * Add object with optional productQuery resolution and optional width/height.
   * Used by the MCP bridge for the enhanced add_object flow.
   */
  async addObjectResolved(params: {
    type: string
    productId?: string
    productQuery?: string
    x: number
    y: number
    width?: number
    height?: number
    rotation?: number
    elevation?: number
    label?: string
    points?: Array<{ x: number; y: number }>
    pathWaypoints?: Array<{ x: number; y: number }>
    pathWidth?: number
    zoneId?: string
  }) {
    this.requireEditMode()
    let productId = params.productId
    let width = params.width
    let height = params.height

    // Resolve product query if no productId
    if (!productId && params.productQuery) {
      const product = await this.resolveProduct(params.productQuery)
      productId = product.id
      // Use product dimensions as defaults
      if (width == null && product.width) width = product.width
      if (height == null && product.height) height = product.height
    }

    if (!productId) {
      throw new Error('Either productId or productQuery is required')
    }

    // If width/height still missing, fetch from product catalog
    if (width == null || height == null) {
      const products = await api.getProducts([productId])
      const product = products.get(productId)
      if (product) {
        if (width == null) width = product.width || 50
        if (height == null) height = product.height || 50
      } else {
        // Fallback: 1m × 1m
        if (width == null) width = 50
        if (height == null) height = 50
      }
    }

    return this.addObject({
      type: params.type,
      productId,
      x: params.x,
      y: params.y,
      width,
      height,
      rotation: params.rotation,
      elevation: params.elevation,
      label: params.label,
      points: params.points,
      pathWaypoints: params.pathWaypoints,
      pathWidth: params.pathWidth,
      zoneId: params.zoneId,
    })
  }

  /**
   * Batch add objects with a single product prefetch for all items.
   * Avoids N sequential API calls that cause timeouts in add_objects.
   */
  async addObjectsBatchResolved(
    objects: Array<Parameters<typeof this.addObjectResolved>[0]>
  ) {
    this.requireEditMode()

    // Step 1: Resolve all productQuery → productId (parallel)
    const resolved = await Promise.all(
      objects.map(async (obj) => {
        if (!obj.productId && obj.productQuery) {
          const product = await this.resolveProduct(obj.productQuery)
          return {
            ...obj,
            productId: product.id,
            width: obj.width ?? product.width,
            height: obj.height ?? product.height,
          }
        }
        return { ...obj }
      })
    )

    // Step 2: Batch-fetch all products that still need width/height
    const idsNeedingDimensions = resolved
      .filter(o => o.productId && (o.width == null || o.height == null))
      .map(o => o.productId!)
    const uniqueIds = [...new Set(idsNeedingDimensions)]
    const productsMap = uniqueIds.length > 0 ? await api.getProducts(uniqueIds) : new Map()

    // Step 3: Place each object (synchronous — no more API calls)
    const results = resolved.map((obj) => {
      try {
        if (!obj.productId) {
          return { success: false as const, error: 'Either productId or productQuery is required' }
        }
        let width = obj.width
        let height = obj.height
        if (width == null || height == null) {
          const product = productsMap.get(obj.productId)
          if (product) {
            if (width == null) width = product.width || 50
            if (height == null) height = product.height || 50
          } else {
            if (width == null) width = 50
            if (height == null) height = 50
          }
        }
        return this.addObject({
          type: obj.type,
          productId: obj.productId,
          x: obj.x,
          y: obj.y,
          width: width ?? 50,
          height: height ?? 50,
          rotation: obj.rotation,
          elevation: obj.elevation,
          label: obj.label,
          points: obj.points,
          pathWaypoints: obj.pathWaypoints,
          pathWidth: obj.pathWidth,
          zoneId: obj.zoneId,
        })
      } catch (e) {
        return { success: false as const, error: e instanceof Error ? e.message : String(e) }
      }
    })

    return { results, count: results.filter(r => r.success).length }
  }

  updateObject(params: { id: string; [key: string]: unknown }) {
    this.requireEditMode()
    const { id, ...updates } = params
    gardenObjectState.updateObject(id, updates)
    return { success: true }
  }

  deleteObject(id: string) {
    this.requireEditMode()
    const obj = gardenObjectState.getObjectById(id)
    if (!obj) return { success: false }
    const snapshot = { ...obj }
    gardenObjectState.deleteObject(id)
    undoStack.push({
      description: `Delete object ${id}`,
      undo: () => gardenObjectState.restoreObject(snapshot),
      redo: () => gardenObjectState.deleteObject(id),
    })
    return { success: true }
  }

  // ── Catalog (ApiClient) ──

  async searchProducts(params: { layer?: string; query?: string; categoryId?: string; page?: number; perPage?: number }) {
    const searchParams: Record<string, unknown> = {}
    if (params.layer) searchParams.layer = params.layer
    if (params.query) searchParams.search = params.query
    if (params.categoryId) searchParams.categoryId = params.categoryId
    if (params.page) searchParams.page = params.page
    if (params.perPage) searchParams.perPage = params.perPage
    const result = await api.searchProducts(searchParams as Parameters<typeof api.searchProducts>[0])
    return {
      products: result.products.map(formatProduct),
      total: result.total,
      page: result.page,
    }
  }

  async getCategories(layer: Layer) {
    const categories = await api.getCategories(layer)
    return categories
  }

  async getProductDetails(productId: string) {
    const products = await api.getProducts([productId])
    const product = products.get(productId)
    if (!product) throw new Error(`Product not found: ${productId}`)
    return formatProduct(product)
  }

  async getPlantInfo(productId: string) {
    const products = await api.getProducts([productId])
    const product = products.get(productId)
    if (!product) throw new Error(`Product not found: ${productId}`)

    const definition = await getPlantDefinition(product.code)
    if (!definition) throw new Error(`Plant definition not available for ${product.code}`)

    return {
      product: formatProduct(product),
      definition,
    }
  }

  // ── Camera & View ──

  capturePreview(_maxSize?: number) {
    const viewer = this.requireViewer()
    const dataUri = viewer.capturePreview()
    if (!dataUri) throw new Error('Failed to capture preview')
    return { dataUri }
  }

  setViewMode(mode: '2d' | '3d') {
    const viewer = this.requireViewer()
    viewer.setViewMode(mode)
    return { success: true, mode }
  }

  getViewMode() {
    const viewer = this.requireViewer()
    return { mode: viewer.getViewMode() }
  }

  focusOn(x: number, y: number, width?: number, height?: number) {
    const viewer = this.requireViewer()
    const scene = viewer.getScene()
    if (!scene) throw new Error('Scene not available')
    scene.focusOn(x, y, 0, width, height)
    return { success: true }
  }

  // ── Layers ──

  setLayerVisible(layer: string, visible: boolean) {
    const viewer = this.requireViewer()
    viewer.setLayerVisible(layer as Parameters<typeof viewer.setLayerVisible>[0], visible)
    return { success: true }
  }

  getLayerVisibility() {
    const viewer = this.requireViewer()
    return viewer.getLayerVisibility()
  }

  // ── Persistence ──

  async save() {
    this.requireEditMode()
    const viewer = this.requireViewer()
    const garden = viewer.getGarden()
    if (!garden) throw new Error('No garden loaded')
    const outline = viewer.getSchemeOutline()
    const success = await saveService.save(garden.id, outline, garden)
    return { success, status: saveService.status }
  }

  // ── Undo/Redo ──

  undo() {
    this.requireEditMode()
    if (!undoStack.canUndo()) return { success: false, canUndo: false, canRedo: undoStack.canRedo() }
    undoStack.undo()
    return { success: true, canUndo: undoStack.canUndo(), canRedo: undoStack.canRedo() }
  }

  redo() {
    this.requireEditMode()
    if (!undoStack.canRedo()) return { success: false, canUndo: undoStack.canUndo(), canRedo: false }
    undoStack.redo()
    return { success: true, canUndo: undoStack.canUndo(), canRedo: undoStack.canRedo() }
  }

  // ── Reference Photos (in-memory session store) ──

  uploadReferencePhoto(dataUri: string, label: string) {
    const id = `photo_${++photoIdCounter}`
    referencePhotos.push({ id, label, dataUri })
    return { id, success: true }
  }

  getReferencePhotos() {
    // Return metadata only (without full dataUri for listing)
    return referencePhotos.map(p => ({ id: p.id, label: p.label, dataUri: p.dataUri }))
  }

  // ── Spatial queries ──

  getObjectsInZone(zoneId: string) {
    const zone = zoneAPI.getZoneById(zoneId)
    if (!zone) throw new Error(`Zone not found: ${zoneId}`)

    const allObjects = gardenObjectState.getObjects()
    const objects = allObjects.filter(obj => isPointInZone(obj.x, obj.y, zone))

    return { objects, count: objects.length }
  }

  getZoneAnalysis(zoneId: string) {
    const zone = zoneAPI.getZoneById(zoneId)
    if (!zone) throw new Error(`Zone not found: ${zoneId}`)

    const { objects: insideObjects } = this.getObjectsInZone(zoneId)
    const zoneArea = calculateZoneArea(zone, DPM)

    // Calculate spread and coverage for each object
    const objectDetails = insideObjects.map(obj => {
      const spread = obj.width / DPM // meters
      return {
        id: obj.id,
        type: obj.type,
        productName: obj.productName ?? obj.type,
        spread,
      }
    })

    const totalSpreadArea = objectDetails.reduce((sum, o) => {
      const r = o.spread / 2
      return sum + Math.PI * r * r
    }, 0)

    const coveragePercent = zoneArea > 0 ? (totalSpreadArea / zoneArea) * 100 : 0

    let density: 'sparse' | 'moderate' | 'good' | 'dense'
    if (coveragePercent < 30) density = 'sparse'
    else if (coveragePercent < 60) density = 'moderate'
    else if (coveragePercent < 90) density = 'good'
    else density = 'dense'

    // Boundary violations: objects outside zone but within 2m buffer
    const BUFFER_M = 2
    const allObjects = gardenObjectState.getObjects()
    const insideIds = new Set(insideObjects.map(o => o.id))
    const warnings: string[] = []

    for (const obj of allObjects) {
      if (insideIds.has(obj.id)) continue
      const distM = distanceToZoneBoundary(obj.x, obj.y, zone, DPM)
      if (distM <= BUFFER_M) {
        const name = obj.productName ?? obj.type
        warnings.push(`${obj.id} (${name}) is ${distM.toFixed(1)}m outside zone boundary`)
      }
    }

    return {
      zone: {
        id: zoneId,
        zoneType: zone.zoneType,
        area: Math.round(zoneArea * 100) / 100,
        label: zone.label ?? null,
      },
      objects: objectDetails,
      analysis: {
        objectCount: objectDetails.length,
        totalSpreadArea: Math.round(totalSpreadArea * 100) / 100,
        coveragePercent: Math.round(coveragePercent * 10) / 10,
        density,
        warnings,
      },
    }
  }

  // ── System ──

  getCoordinateSystem() {
    return {
      dotsPerMeter: DPM,
      origin: 'top-left',
      axes: { x: 'rightward', y: 'downward' },
      rotationUnit: 'degrees',
      rotationDirection: 'clockwise',
      elevationUnit: 'meters',
    }
  }
}

/**
 * Format a Product for MCP responses (strip internal fields)
 */
function formatProduct(product: Product) {
  return {
    id: product.id,
    code: product.code,
    name: product.name,
    latinName: product.latinName,
    mainCategoryId: product.mainCategoryId,
    elevation: product.elevation,
    width: product.width,
    height: product.height,
    iconUrl: product.iconUrl,
  }
}

/** Singleton */
export const editorAPI = new EditorAPIManager()
